import React, { useEffect, useRef, useState } from 'react';
import config from '../config';
import { buildApiUrl, fetchApiJson } from '../utils/apiUrl';
import {
  optimizeFabricImageUrl,
  optimizeFabricImageList,
  preloadFabricImages,
  FABRIC_CARD_IMAGE_OPTS,
  FABRIC_GALLERY_IMAGE_OPTS
} from '../utils/fabricImageUrl';

const API_BASE = config.API_BASE;
const imageCache = new Map();
const HOVER_IMAGE_INTERVAL_MS = 500;

function getInitialImages(fabric) {
  if (fabric.image_url) {
    return [optimizeFabricImageUrl(fabric.image_url, FABRIC_CARD_IMAGE_OPTS)];
  }
  return [];
}

function getHoverZone(event, element) {
  const rect = element.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const third = rect.width / 3;
  if (x < third) return 'left';
  if (x > third * 2) return 'right';
  return 'center';
}

function directionForZone(zone) {
  if (zone === 'left') return -1;
  if (zone === 'right') return 1;
  return 0;
}

async function fetchFabricImages(fabricId) {
  if (imageCache.has(fabricId)) return imageCache.get(fabricId);
  const data = await fetchApiJson(buildApiUrl(API_BASE, `/api/fabrics/${fabricId}/images`));
  const urls = optimizeFabricImageList(data.image_urls || [], FABRIC_GALLERY_IMAGE_OPTS);
  imageCache.set(fabricId, urls);
  preloadFabricImages(urls, FABRIC_GALLERY_IMAGE_OPTS);
  return urls;
}

export default function FabricCardImageHover({ fabric, name, priority = false }) {
  const [images, setImages] = useState(() => getInitialImages(fabric));
  const [index, setIndex] = useState(0);
  const [zone, setZone] = useState('center');
  const wrapRef = useRef(null);
  const intervalRef = useRef(null);
  const directionRef = useRef(0);

  function clearTimer() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  function startTimer(direction, imageCount) {
    clearTimer();
    directionRef.current = direction;
    if (direction === 0 || imageCount <= 1) return;

    intervalRef.current = setInterval(() => {
      setIndex((prev) => {
        if (direction < 0) {
          return prev <= 0 ? imageCount - 1 : prev - 1;
        }
        return prev >= imageCount - 1 ? 0 : prev + 1;
      });
    }, HOVER_IMAGE_INTERVAL_MS);
  }

  function handlePointerMove(event) {
    if (!wrapRef.current || images.length <= 1) return;
    const nextZone = getHoverZone(event, wrapRef.current);
    const nextDirection = directionForZone(nextZone);
    setZone(nextZone);
    if (nextDirection !== directionRef.current) {
      startTimer(nextDirection, images.length);
    }
  }

  async function handlePointerEnter(event) {
    let activeImages = images;
    if (activeImages.length <= 1 && fabric.id) {
      try {
        const urls = await fetchFabricImages(fabric.id);
        if (urls.length) {
          setImages(urls);
          activeImages = urls;
        }
      } catch {
        // keep thumbnail only
      }
    }
    if (!wrapRef.current || activeImages.length <= 1) return;
    const nextZone = getHoverZone(event, wrapRef.current);
    setZone(nextZone);
    startTimer(directionForZone(nextZone), activeImages.length);
  }

  function handlePointerLeave() {
    clearTimer();
    directionRef.current = 0;
    setZone('center');
    setIndex(0);
    setImages(getInitialImages(fabric));
  }

  useEffect(() => {
    const nextImages = getInitialImages(fabric);
    setImages(nextImages);
    setIndex(0);
    preloadFabricImages(nextImages, FABRIC_CARD_IMAGE_OPTS);
  }, [fabric.id, fabric.image_url]);

  useEffect(() => () => clearTimer(), []);

  if (!images.length && !fabric.image_url) {
    return <div className="fabric-card-image fabric-card-image--placeholder">No image</div>;
  }

  const currentImage = images[Math.min(index, images.length - 1)] || optimizeFabricImageUrl(fabric.image_url, FABRIC_CARD_IMAGE_OPTS);

  return (
    <div
      ref={wrapRef}
      className={`fabric-card-image-wrap fabric-card-image-wrap--interactive fabric-card-image-wrap--zone-${zone}`}
      onMouseEnter={handlePointerEnter}
      onMouseMove={handlePointerMove}
      onMouseLeave={handlePointerLeave}
    >
      <img
        src={currentImage}
        alt={name}
        className="fabric-card-image"
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
      />
      {images.length > 1 && (
        <>
          <div className="fabric-card-image-zone fabric-card-image-zone--left" aria-hidden="true" />
          <div className="fabric-card-image-zone fabric-card-image-zone--center" aria-hidden="true" />
          <div className="fabric-card-image-zone fabric-card-image-zone--right" aria-hidden="true" />
          <div className="fabric-card-image-dots" aria-hidden="true">
            {images.map((img, dotIndex) => (
              <span
                key={`${img}-${dotIndex}`}
                className={`fabric-card-image-dot${dotIndex === index ? ' fabric-card-image-dot--active' : ''}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
